import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Toast from '../../Shared/Toast';
import Modal from '../../Shared/Modal';
import styles from './Ranking.module.css';

interface Mercenary {
  id: number;
  name: string;
  race: string;
  level: number;
  prestigeLevel?: number;
  experience: number;
  kills: number;
  title?: string | null;
  deathDate?: string | null;
  killedBy?: string | null;
  Guild?: {
    id: number;
    name: string;
    tag: string;
  };
}

interface GuildEntry {
  id: number;
  name: string;
  tag: string;
  level: number;
  exp: number;
  totalExp: number;
  totalMembers: number;
  leaguePosition: number | null;
  leaguePoints: number | null;
  leagueWins: number | null;
  leagueLosses: number | null;
}

interface FallenMercenary {
  id: number;
  name: string;
  race: string;
  level: number;
  prestigeLevel?: number;
  experience: number;
  kills: number;
  title?: string | null;
  deathDate: string;
  killedBy: string;
  deathBattleReportId?: number | null;
  Guild?: {
    id: number;
    name: string;
    tag: string;
  };
}

type Tab = 'mercenaries' | 'guilds' | 'fallen' | 'ranked';

const Ranking: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('mercenaries');

  // Mercenary state
  const [mercenaries, setMercenaries] = useState<Mercenary[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [seasonActive, setSeasonActive] = useState<boolean>(true);
  const [raceFilter, setRaceFilter] = useState<string>('all');
  const [availableRaces, setAvailableRaces] = useState<string[]>([]);

  // Guild state
  const [guilds, setGuilds] = useState<GuildEntry[]>([]);
  const [guildPage, setGuildPage] = useState<number>(1);
  const [guildTotalPages, setGuildTotalPages] = useState<number>(1);
  const [guildLoading, setGuildLoading] = useState<boolean>(false);

  // Fallen state
  const [fallen, setFallen] = useState<FallenMercenary[]>([]);
  const [fallenPage, setFallenPage] = useState<number>(1);
  const [fallenTotalPages, setFallenTotalPages] = useState<number>(1);
  const [fallenLoading, setFallenLoading] = useState<boolean>(false);
  const [fallenSeasonActive, setFallenSeasonActive] = useState<boolean>(true);

  // Ranked state
  const [rankedBracket, setRankedBracket] = useState<string>('2v2');
  const [rankedLeaderboard, setRankedLeaderboard] = useState<any[]>([]);
  const [rankedPage, setRankedPage] = useState<number>(1);
  const [rankedTotalPages, setRankedTotalPages] = useState<number>(1);
  const [rankedLoading, setRankedLoading] = useState<boolean>(false);

  // Mordren modal state machine
  type MordrenStage = 'idle' | 'loading' | 'quote' | 'stage2' | 'done';
  const [mordrenStage, setMordrenStage] = useState<MordrenStage>('idle');
  const [mordrenText, setMordrenText] = useState<string>('');
  const [mordrenAltButton, setMordrenAltButton] = useState<boolean>(false);
  const [mordrenToken, setMordrenToken] = useState<string>('');
  const [mordrenToken2, setMordrenToken2] = useState<string>('');

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  useEffect(() => {
    fetchRanking(currentPage, raceFilter);
  }, [currentPage, raceFilter]);

  useEffect(() => {
    if (activeTab === 'guilds') fetchGuildRanking(guildPage);
  }, [activeTab, guildPage]);

  useEffect(() => {
    if (activeTab === 'fallen') fetchFallen(fallenPage);
  }, [activeTab, fallenPage]);

  useEffect(() => {
    if (activeTab === 'ranked') fetchRankedLeaderboard(rankedBracket, rankedPage);
  }, [activeTab, rankedBracket, rankedPage]);

  const fetchRanking = async (page: number, race: string = 'all') => {
    setLoading(true);
    try {
      const response = await axios.get('/api/mercenary/ranking', {
        params: { page, ...(race !== 'all' ? { race } : {}) },
        withCredentials: true,
      });
      setMercenaries(response.data.mercenaries);
      setTotalPages(response.data.totalPages);
      setSeasonActive(response.data.seasonActive !== false);
      if (response.data.availableRaces) setAvailableRaces(response.data.availableRaces);
    } catch (error) {
      console.error('Error fetching ranking:', error);
      displayToast('Failed to load mercenary rankings. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchGuildRanking = async (page: number) => {
    setGuildLoading(true);
    try {
      const response = await axios.get('/api/guild/ranking', {
        params: { page },
        withCredentials: true,
      });
      setGuilds(response.data.guilds);
      setGuildTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching guild ranking:', error);
      displayToast('Failed to load guild rankings. Please try again later.', 'error');
    } finally {
      setGuildLoading(false);
    }
  };

  const fetchFallen = async (page: number) => {
    setFallenLoading(true);
    try {
      const response = await axios.get('/api/mercenary/fallen', {
        params: { page },
        withCredentials: true,
      });
      setFallen(response.data.fallen);
      setFallenTotalPages(response.data.totalPages);
      setFallenSeasonActive(response.data.seasonActive !== false);
    } catch (error) {
      console.error('Error fetching fallen mercenaries:', error);
      displayToast('Failed to load the Quiet Stones. Please try again later.', 'error');
    } finally {
      setFallenLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const handleRaceFilterChange = (race: string) => {
    setRaceFilter(race);
    setCurrentPage(1);
  };

  const handleGuildPageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= guildTotalPages) setGuildPage(newPage);
  };

  const handleFallenPageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= fallenTotalPages) setFallenPage(newPage);
  };

  const fetchRankedLeaderboard = async (bracket: string, page: number) => {
    setRankedLoading(true);
    try {
      const response = await axios.get(`/api/ranked/leaderboard/${bracket}`, {
        params: { page, limit: 25 },
        withCredentials: true,
      });
      setRankedLeaderboard(response.data.leaderboard);
      setRankedTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching ranked leaderboard:', error);
      displayToast('Failed to load ranked leaderboard.', 'error');
    } finally {
      setRankedLoading(false);
    }
  };

  const handleRankedPageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= rankedTotalPages) setRankedPage(newPage);
  };

  const handleRankedBracketChange = (bracket: string) => {
    setRankedBracket(bracket);
    setRankedPage(1);
  };

  const openMordrenModal = async () => {
    setMordrenStage('loading');
    try {
      const res = await axios.get('/api/mercenary/gravekeeper/echo', { withCredentials: true });
      setMordrenText(res.data.text);
      setMordrenToken(res.data.token);
      setMordrenAltButton(!!res.data.altButton);
      setMordrenStage('quote');
    } catch {
      setMordrenStage('idle');
    }
  };

  const reactToMordren = async () => {
    try {
      const res = await axios.post('/api/mercenary/gravekeeper/stir', { token: mordrenToken }, { withCredentials: true });
      setMordrenText(res.data.text);
      setMordrenToken2(res.data.token2);
      setMordrenStage('stage2');
    } catch {
      setMordrenStage('idle');
    }
  };

  const fleeMordren = async () => {
    setMordrenStage('idle');
    try {
      await axios.post('/api/mercenary/gravekeeper/leave', { token2: mordrenToken2 }, { withCredentials: true });
    } catch { /* silent */ }
  };

  const closeMordrenModal = () => setMordrenStage('idle');

  return (
    <div className={styles.rankingContainer}>
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          duration={3000}
          onClose={() => setToastMessage(null)}
        />
      )}
      <h1 className={styles.rankingTitle}>
        {activeTab === 'fallen' ? 'The Quiet Stones' : 'Leaderboard'}
      </h1>
      {activeTab === 'fallen' ? (
        <button
          className={`${styles.rankingSubtitle} ${styles.rankingSubtitleClickable}`}
          onClick={openMordrenModal}
        >
          Old Mordren the Gravekeeper
        </button>
      ) : (
        <p className={styles.rankingSubtitle}>Season standings</p>
      )}

      {/* Tab navigation */}
      <div className={styles.tabNav}>
        <button
          className={`${styles.tabButton} ${activeTab === 'mercenaries' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('mercenaries')}
        >
          Mercenaries
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'guilds' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('guilds')}
        >
          Guilds
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'ranked' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('ranked')}
        >
          Ranked
        </button>
        <div className={styles.tabNavSpacer} />
        <button
          className={`${styles.tabButton} ${styles.tabButtonFallen} ${activeTab === 'fallen' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('fallen')}
        >
          The Quiet Stones
        </button>
      </div>

      {/* Mercenary tab */}
      {activeTab === 'mercenaries' && (
        loading ? <p>Loading...</p> : (
          <>
            {!seasonActive && (
              <div className={styles.offSeasonNotice}>
                The game is currently in off-season. All mercenaries from the last season are listed, dead and retired included.
              </div>
            )}
            <div className={styles.tableWrapper}>
            <table className={styles.rankingTable}>
              <colgroup>
                <col style={{ width: '75px' }} />
                <col />
                <col style={{ width: '120px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '75px' }} />
                <col style={{ width: '150px' }} />
                <col style={{ width: '75px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Name</th>
                  <th>
                    <select
                      className={styles.raceSelect}
                      value={raceFilter}
                      onChange={e => handleRaceFilterChange(e.target.value)}
                    >
                      <option value="all">Race</option>
                      {availableRaces.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </th>
                  <th>Guild</th>
                  <th>Level</th>
                  <th>Experience</th>
                  <th>Kills</th>
                </tr>
              </thead>
              <tbody>
                {mercenaries.map((mercenary, index) => (
                  <tr key={mercenary.id}>
                    <td>{(currentPage - 1) * 20 + index + 1}</td>
                    <td>
                      <Link
                        to={`/mercenary/${mercenary.id}`}
                        className={`${styles.profileLink}${mercenary.title === 'Legend' ? ' ' + styles.legendName : ''}`}
                      >
                        {mercenary.name}{mercenary.killedBy === 'Retired' ? ' (Retired)' : mercenary.deathDate ? ' (Dead)' : ''}
                      </Link>
                    </td>
                    <td>{mercenary.race}</td>
                    <td>
                      {mercenary.Guild ? (
                        <Link to={`/guild/${mercenary.Guild.id}`} className={styles.guildLink}>
                          [{mercenary.Guild.tag}] {mercenary.Guild.name}
                        </Link>
                      ) : (
                        <span className={styles.noGuild}>-</span>
                      )}
                    </td>
                    <td>{mercenary.level}{mercenary.prestigeLevel ? ` (+${mercenary.prestigeLevel})` : ''}</td>
                    <td>{mercenary.experience.toLocaleString('sv-SE')}</td>
                    <td>{mercenary.kills}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className={styles.paginationContainer}>
              <button className={styles.paginationButton} onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
              <span className={styles.paginationInfo}>{currentPage}/{totalPages}</span>
              <button className={styles.paginationButton} onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
            </div>
          </>
        )
      )}

      {/* Guild tab */}
      {activeTab === 'guilds' && (
        guildLoading ? <p>Loading...</p> : (
          <>
            <div className={styles.tableWrapper}>
            <table className={styles.rankingTable}>
              <colgroup>
                <col style={{ width: '55px' }} />
                <col />
                <col style={{ width: '65px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '85px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '105px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Guild</th>
                  <th>Level</th>
                  <th>Total EXP</th>
                  <th>Members</th>
                  <th>League Pos.</th>
                  <th>League (W/L)</th>
                </tr>
              </thead>
              <tbody>
                {guilds.map((guild, index) => (
                  <tr key={guild.id}>
                    <td>{(guildPage - 1) * 20 + index + 1}</td>
                    <td>
                      <Link to={`/guild/${guild.id}`} className={styles.guildLink}>
                        [{guild.tag}] {guild.name}
                      </Link>
                    </td>
                    <td>{guild.level}</td>
                    <td>{(guild.totalExp || guild.exp).toLocaleString('sv-SE')}</td>
                    <td>{guild.totalMembers}</td>
                    <td>
                      {guild.leaguePosition != null
                        ? <span className={styles.leaguePos}>#{guild.leaguePosition}</span>
                        : <span className={styles.noGuild}>-</span>}
                    </td>
                    <td>
                      {guild.leagueWins != null
                        ? `${guild.leagueWins}/${guild.leagueLosses}`
                        : <span className={styles.noGuild}>-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className={styles.paginationContainer}>
              <button className={styles.paginationButton} onClick={() => handleGuildPageChange(guildPage - 1)} disabled={guildPage === 1}>Previous</button>
              <span className={styles.paginationInfo}>{guildPage}/{guildTotalPages}</span>
              <button className={styles.paginationButton} onClick={() => handleGuildPageChange(guildPage + 1)} disabled={guildPage === guildTotalPages}>Next</button>
            </div>
          </>
        )
      )}

      {/* The Quiet Stones tab */}
      {activeTab === 'fallen' && (
        <>
          <div className={styles.quietStonesLore}>
            <p>The Quiet Stones mark the resting place of those who did not leave the arena alive. Their markers stand in long rows of cold granite, carved with names the crowds once chanted.</p>
            <p>Here, the cheering ends. Here, the boasting ends.</p>
            <p>Only the stones remain, quiet, patient, and unmoved by the passing of yet another fallen warrior.</p>
            <p>Old Mordren the Gravekeeper tends the grounds, digging the earth and laying the fallen to rest with a silence that suggests he has long since joined them.</p>
          </div>

          {/* Mordren modal, state machine */}
          <Modal
            show={mordrenStage === 'loading' || mordrenStage === 'quote' || mordrenStage === 'stage2'}
            onClose={closeMordrenModal}
            title="Old Mordren the Gravekeeper"
            hideCloseButton={true}
            message={
              mordrenStage === 'loading'
                ? <p style={{ fontStyle: 'italic', opacity: 0.5 }}>…</p>
                : <p style={{ fontStyle: 'italic', lineHeight: '1.9', whiteSpace: 'pre-line' }}>
                    {mordrenText}
                  </p>
            }
            customButtons={
              mordrenStage === 'loading'
                ? []
                : mordrenStage === 'quote'
                ? mordrenAltButton
                  ? [
                      { text: 'Turn Around Quick!', action: reactToMordren },
                      { text: 'Nod politely… Look the other way.', action: closeMordrenModal },
                    ]
                  : [{ text: 'Nod politely… Look the other way.', action: closeMordrenModal }]
                : mordrenStage === 'stage2'
                  ? [{ text: 'It might be best to leave the Quiet Stones for now..\nRunning!', action: fleeMordren }]
                  : []
            }
          />

          {fallenLoading ? <p>Loading...</p> : !fallenSeasonActive ? (
            <div className={styles.offSeasonNotice}>There is no active season. The stones are quiet for now.</div>
          ) : (
            <>
              <div className={styles.tableWrapper}>
              <table className={styles.rankingTable}>
                <colgroup>
                  <col style={{ width: '55px' }} />
                  <col />
                  <col style={{ width: '110px' }} />
                  <col style={{ width: '160px' }} />
                  <col style={{ width: '80px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '145px' }} />
                  <col style={{ width: '110px' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Race</th>
                    <th>Guild</th>
                    <th>Level</th>
                    <th>Killed By</th>
                    <th>Date</th>
                    <th>Battle</th>
                  </tr>
                </thead>
                <tbody>
                  {fallen.map((merc, index) => (
                    <tr key={merc.id}>
                      <td>{(fallenPage - 1) * 20 + index + 1}</td>
                      <td>
                        <Link
                          to={`/mercenary/${merc.id}`}
                          className={`${styles.profileLink}${merc.title === 'Legend' ? ' ' + styles.legendName : ''}`}
                        >
                          {merc.name}
                        </Link>
                      </td>
                      <td>{merc.race}</td>
                      <td>
                        {merc.Guild ? (
                          <Link to={`/guild/${merc.Guild.id}`} className={styles.guildLink}>
                            [{merc.Guild.tag}] {merc.Guild.name}
                          </Link>
                        ) : (
                          <span className={styles.noGuild}>-</span>
                        )}
                      </td>
                      <td>{merc.level}{merc.prestigeLevel ? ` (+${merc.prestigeLevel})` : ''}</td>
                      <td className={styles.killedBy}>{merc.killedBy}</td>
                      <td>{new Date(merc.deathDate).toLocaleDateString('sv-SE')}</td>
                      <td>
                        {merc.deathBattleReportId ? (
                          <Link to={`/battle-result/${merc.deathBattleReportId}`} className={styles.profileLink}>
                            View Battle
                          </Link>
                        ) : (
                          <span className={styles.noGuild}>-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {fallen.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                        No warriors have fallen this season.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
              {fallenTotalPages > 1 && (
                <div className={styles.paginationContainer}>
                  <button className={styles.paginationButton} onClick={() => handleFallenPageChange(fallenPage - 1)} disabled={fallenPage === 1}>Previous</button>
                  <span className={styles.paginationInfo}>{fallenPage}/{fallenTotalPages}</span>
                  <button className={styles.paginationButton} onClick={() => handleFallenPageChange(fallenPage + 1)} disabled={fallenPage === fallenTotalPages}>Next</button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Ranked tab */}
      {activeTab === 'ranked' && (
        <>
          <div className={styles.rankedBracketTabs}>
            {['2v2', '3v3', '4v4'].map(b => (
              <button
                key={b}
                className={`${styles.bracketTab} ${rankedBracket === b ? styles.bracketTabActive : ''}`}
                onClick={() => handleRankedBracketChange(b)}
              >
                {b}
              </button>
            ))}
          </div>

          {rankedLoading ? <p>Loading...</p> : (
            <>
              <div className={styles.tableWrapper}>
              <table className={styles.rankingTable}>
                <colgroup>
                  <col style={{ width: '55px' }} />
                  <col />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '80px' }} />
                  <col style={{ width: '80px' }} />
                  <col style={{ width: '80px' }} />
                  <col style={{ width: '80px' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Race</th>
                    <th>Rating</th>
                    <th>W</th>
                    <th>L</th>
                    <th>Win%</th>
                    <th>Peak</th>
                  </tr>
                </thead>
                <tbody>
                  {rankedLeaderboard.map((entry) => (
                    <tr key={entry.mercenaryId} className={entry.isDead ? styles.deadRow : ''}>
                      <td>{entry.rank}</td>
                      <td>
                        <Link
                          to={`/mercenary/${entry.mercenaryId}`}
                          className={`${styles.profileLink}${entry.title === 'Legend' ? ' ' + styles.legendName : ''}`}
                        >
                          {entry.mercenaryName}
                        </Link>
                      </td>
                      <td>{entry.race || '-'}</td>
                      <td className={styles.ratingCell}>{entry.rating}</td>
                      <td>{entry.wins}</td>
                      <td>{entry.losses}</td>
                      <td>{entry.winRate}%</td>
                      <td>{entry.peakRating}</td>
                    </tr>
                  ))}
                  {rankedLeaderboard.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                        No ranked battles have been fought in {rankedBracket} yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
              {rankedTotalPages > 1 && (
                <div className={styles.paginationContainer}>
                  <button className={styles.paginationButton} onClick={() => handleRankedPageChange(rankedPage - 1)} disabled={rankedPage === 1}>Previous</button>
                  <span className={styles.paginationInfo}>{rankedPage}/{rankedTotalPages}</span>
                  <button className={styles.paginationButton} onClick={() => handleRankedPageChange(rankedPage + 1)} disabled={rankedPage === rankedTotalPages}>Next</button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Ranking;
